import React, { useRef } from 'react';
import { motion, useInView } from "framer-motion";
import { Link } from 'react-router-dom';
import { 
  FaRocket, 
  FaUsers, 
  FaLightbulb, 
  FaChartLine, 
  FaGlobe,
  FaLeaf,
  FaSeedling,
  FaTint,
  FaApple,
  FaBullseye,
  FaHandsHelping,
  FaCheckCircle,
  FaAward,
  FaFlask,
  FaShoppingBag,
  FaCalendarAlt,
  FaTrophy,
  FaHeart,
  FaStar,
  FaGem
} from 'react-icons/fa';
import aboutImage from "../img/Terra Gold Stand-Up Bag Mockup.png";
import '../css/Aboutusmain.css';

const Aboutus = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const fadeIn = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const journeyMilestones = [
    { year: "2019", title: "The Beginning", description: "TerraGold was founded with a vision to make high-quality natural nutrition accessible to everyone." },
    { year: "2020", title: "First Products", description: "Launched our initial range of premium nutraceutical and plant-based products." },
    { year: "2021", title: "Expansion", description: "Expanded capabilities and refined manufacturing processes for better quality." },
    { year: "2022", title: "Quality Excellence", description: "Strengthened quality standards and earned trust of partners and customers." },
    { year: "2023", title: "Growing Strong", description: "Became a trusted name with a growing range of organic wellness products." },
    { year: "2024", title: "Today & Beyond", description: "Continuing our commitment to quality, integrity, and natural nutrition." }
  ];

  const keyFeatures = [
    {
      icon: <FaLeaf />,
      title: "100% Natural Ingredients",
      description: "Carefully sourced from trusted farms to ensure purity and authenticity.",
      color: "#22c55e"
    },
    {
      icon: <FaFlask />,
      title: "No Artificial Additives",
      description: "Free from artificial colors, flavors, preservatives, and fillers.",
      color: "#06b6d4"
    },
    {
      icon: <FaSeedling />,
      title: "Nutrient-Rich Processing",
      description: "Advanced manufacturing methods designed to preserve natural nutrients.",
      color: "#84cc16"
    },
    {
      icon: <FaCheckCircle />,
      title: "Quality Tested",
      description: "Every batch undergoes strict quality and safety checks.",
      color: "#10b981"
    },
    {
      icon: <FaAward />,
      title: "GMP & FSSAI Compliant",
      description: "Manufactured in facilities following recognized quality standards.",
      color: "#f59e0b"
    },
    {
      icon: <FaShoppingBag />,
      title: "Farm-to-Pack Freshness",
      description: "From sourcing to packaging, we maintain quality at every step.",
      color: "#3b82f6"
    }
  ];

  return (
    <div className="lissom-about-container" ref={ref}>
      {/* Animated Hero Section */}
      <motion.div 
        className="lissom-about-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="lissom-hero-overlay"></div>
        <div className="lissom-hero-content">
          <motion.h1 
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            About <span className="lissom-text-gradient">Terra Gold</span>
          </motion.h1>
          <motion.p
            className="lissom-hero-slogan"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <span className="lissom-highlight">Nature's Purest Nutrition</span>, Crafted for Better Living
          </motion.p>
          <motion.p
            className="lissom-hero-description"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            Experience the power of nature with our carefully crafted organic nutraceutical products. 
            Sourced from quality ingredients and processed to preserve maximum nutrition for your daily wellness journey.
          </motion.p>
          <motion.div
            className="lissom-scroll-indicator"
            animate={{ 
              y: [0, 15, 0],
              opacity: [1, 0.5, 1]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 2,
              ease: "easeInOut"
            }}
          >
            <div className="lissom-scroll-line"></div>
          </motion.div>
        </div>
        <div className="lissom-hero-particles">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="particle"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 0.6, 0],
                y: [0, -Math.random() * 100 - 50],
                x: [0, (Math.random() - 0.5) * 100]
              }}
              transition={{
                duration: 3 + Math.random() * 5,
                repeat: Infinity,
                delay: Math.random() * 3
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 2}px`,
                height: `${Math.random() * 10 + 2}px`,
                background: `hsl(${Math.random() * 60 + 40}, 80%, 60%)`
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="lissom-about-main">
        {/* Company Introduction */}
        <motion.section 
          className="lissom-company-intro"
          variants={staggerContainer}
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 order-lg-2">
                <motion.div 
                  className="lissom-image-container"
                  variants={item}
                >
                  <div className="lissom-image-frame"></div>
                  <img 
                    src={aboutImage} 
                    alt="Terra Gold" 
                    className="img-fluid rounded"
                  />
                  <div className="lissom-image-badge">
                    <FaGem className="badge-icon" /> Pure & Natural
                  </div>
                </motion.div>
              </div>
              <div className="col-lg-6 order-lg-1">
                <motion.div variants={item}>
                  <motion.h2
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="lissom-section-subtitle"
                  >
                    ABOUT TERRAGOLD
                  </motion.h2>
                  <motion.h1
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    Pioneering <span className="lissom-text-gradient">Natural Nutrition</span>
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="lissom-company-description"
                  >
                    <strong>TerraGold</strong> is a premium nutraceutical manufacturer committed to delivering 
                    high-quality organic and plant-based wellness products. By combining nature's finest 
                    ingredients with modern manufacturing standards, we create trusted nutritional solutions 
                    that support healthier lifestyles and everyday well-being.
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    className="lissom-company-description"
                  >
                    At TerraGold, we believe true wellness begins with nature. We are dedicated to manufacturing 
                    premium-quality organic nutraceuticals and superfood products sourced from carefully selected 
                    ingredients. Through advanced processing and strict quality standards, we preserve the natural 
                    goodness of every product, delivering purity, nutrition, and trust to support healthier 
                    lifestyles worldwide.
                  </motion.p>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Company History / Journey Timeline */}
        <section className="lissom-journey-section">
          <div className="container">
            <motion.div
              className="lissom-section-header"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h1>Our <span className="lissom-text-gradient">Journey</span></h1>
              <p style={{textAlign:"left"}}>Founded in 2019, TerraGold began with a simple vision—to make high-quality natural nutrition accessible to everyone</p>
            </motion.div>

            <div className="lissom-timeline">
              {journeyMilestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  className={`lissom-timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="lissom-timeline-content">
                    <div className="lissom-timeline-year">{milestone.year}</div>
                    <h3>{milestone.title}</h3>
                    <p style={{textAlign:"left"}}>{milestone.description}</p>
                  </div>
                  <div className="lissom-timeline-dot">
                    <div className="lissom-timeline-inner-dot"></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Stats */}
        <motion.section 
          className="lissom-company-stats"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="container">
            <div className="row g-4">
              <div className="col-md-3 col-6">
                <motion.div 
                  className="lissom-stat-card"
                  whileHover={{ y: -10 }}
                >
                  <div className="stat-icon"><FaCalendarAlt /></div>
                  <div className="stat-number">5+</div>
                  <div className="stat-label">Years of Excellence</div>
                </motion.div>
              </div>
              <div className="col-md-3 col-6">
                <motion.div 
                  className="lissom-stat-card"
                  whileHover={{ y: -10 }}
                >
                  <div className="stat-icon"><FaLeaf /></div>
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Natural Products</div>
                </motion.div>
              </div>
              <div className="col-md-3 col-6">
                <motion.div 
                  className="lissom-stat-card"
                  whileHover={{ y: -10 }}
                >
                  <div className="stat-icon"><FaUsers /></div>
                  <div className="stat-number">10K+</div>
                  <div className="stat-label">Happy Customers</div>
                </motion.div>
              </div>
              <div className="col-md-3 col-6">
                <motion.div 
                  className="lissom-stat-card"
                  whileHover={{ y: -10 }}
                >
                  <div className="stat-icon"><FaTrophy /></div>
                  <div className="stat-number">92%</div>
                  <div className="stat-label">Customer Retention</div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Mission & Vision */}
        <section className="lissom-mission-vision">
          <div className="container">
            <motion.div
              className="lissom-section-header"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h1>Our <span className="lissom-text-gradient">Mission & Vision</span></h1>
              <p>Driven by purpose, guided by values</p>
            </motion.div>

            <div className="row g-4">
              <div className="col-md-6">
                <motion.div 
                  className="lissom-mission-card"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                >
                  <div className="card-icon-wrapper mission-icon">
                    <FaBullseye />
                  </div>
                  <h2>Our Mission</h2>
                  <p style={{textAlign:"left"}}>
                    At TerraGold, our mission is to unlock the power of nature and transform it into 
                    high-quality nutritional products that support healthier lives. We are committed to 
                    sourcing the finest natural ingredients, maintaining the highest standards of quality, 
                    and delivering products that consumers can trust every day.
                  </p>
                  <p style={{textAlign:"left"}}>
                    We believe that good health begins with pure nutrition. Through innovation, responsible 
                    manufacturing, and an unwavering dedication to excellence, we strive to make natural 
                    wellness accessible to everyone. Our goal is not only to create exceptional products 
                    but also to inspire a healthier, more sustainable future for generations to come.
                  </p>
                  <div className="mission-tags">
                    <span><FaCheckCircle /> Quality First</span>
                    <span><FaCheckCircle /> Innovation</span>
                    <span><FaCheckCircle /> Sustainability</span>
                  </div>
                </motion.div>
              </div>
              <div className="col-md-6">
                <motion.div 
                  className="lissom-vision-card"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                >
                  <div className="card-icon-wrapper vision-icon">
                    <FaStar />
                  </div>
                  <h2>Our Vision</h2>
                  <div className="vision-statement">
                    <FaGem className="vision-gem" />
                    <p style={{textAlign:"left"}}>
                      To become the most trusted name in natural wellness, enriching lives through the 
                      power of pure nutrition.
                    </p>
                  </div>
                  <div className="vision-goals">
                    <h4>What We Aspire To:</h4>
                    <ul>
                      <li><FaCheckCircle /> Be the most trusted wellness brand globally</li>
                      <li><FaCheckCircle /> Set new standards in natural nutrition</li>
                      <li><FaCheckCircle /> Inspire healthier, sustainable living</li>
                      <li><FaCheckCircle /> Enrich millions of lives naturally</li>
                    </ul>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values - Premium Design */}
        <section className="lissom-mission-values">
          <div className="container">
            <motion.div
              className="lissom-section-header"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h1>Our <span className="lissom-text-gradient">Core Values</span></h1>
              <p style={{textAlign:"left"}}>The principles that guide every product we create and every life we touch</p>
            </motion.div>

            <motion.div 
              className="lissom-values-grid-premium"
              initial="hidden"
              whileInView="visible"
              variants={staggerContainer}
              viewport={{ once: true }}
            >
              {keyFeatures.map((value, index) => (
                <motion.div 
                  key={index}
                  className="lissom-value-card-premium"
                  variants={item}
                  style={{ 
                    '--lissom-card-color': value.color,
                    '--lissom-card-gradient': `linear-gradient(135deg, ${value.color}, ${value.color}dd)`,
                    '--lissom-card-bg': `linear-gradient(135deg, ${value.color}15, ${value.color}08)`
                  }}
                  whileHover={{ 
                    y: -15,
                    boxShadow: `0 25px 50px -12px ${value.color}60`
                  }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="lissom-card-decor"></div>
                  <div className="lissom-card-shine"></div>
                  
                  <div className="lissom-value-icon-wrapper">
                    <div className="lissom-value-icon-premium">
                      {value.icon}
                    </div>
                    <div className="lissom-icon-ring"></div>
                    <div className="lissom-icon-pulse"></div>
                  </div>
                  
                  <div className="lissom-value-content">
                    <h3>{value.title}</h3>
                    <div className="lissom-value-underline"></div>
                    <p style={{textAlign:"left"}}>{value.description}</p>
                  </div>
                  
                  <div className="lissom-value-number">{String(index + 1).padStart(2, '0')}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Why Choose TerraGold */}
        <section className="lissom-why-choose">
          <div className="container">
            <motion.div
              className="lissom-section-header"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h1>Why Choose <span className="lissom-text-gradient">TerraGold</span></h1>
              <p style={{textAlign:"center"}}>Quality, trust, and nature's purity in every product</p>
            </motion.div>

            <div className="row g-4">
              <div className="col-md-4">
                <motion.div 
                  className="lissom-choose-card"
                  whileHover={{ y: -10 }}
                >
                  <div className="choose-icon green">
                    <FaHeart />
                  </div>
                  <h3>100% Natural</h3>
                  <p style={{textAlign:"left"}}>All our products are made from pure, natural ingredients sourced from trusted farms.</p>
                </motion.div>
              </div>
              <div className="col-md-4">
                <motion.div 
                  className="lissom-choose-card"
                  whileHover={{ y: -10 }}
                >
                  <div className="choose-icon gold">
                    <FaAward />
                  </div>
                  <h3>Quality Assured</h3>
                  <p style={{textAlign:"left"}}>Every batch undergoes rigorous quality testing to ensure purity and potency.</p>
                </motion.div>
              </div>
              <div className="col-md-4">
                <motion.div 
                  className="lissom-choose-card"
                  whileHover={{ y: -10 }}
                >
                  <div className="choose-icon blue">
                    <FaGlobe />
                  </div>
                  <h3>Trusted Worldwide</h3>
                  <p style={{textAlign:"left"}}>Thousands of satisfied customers trust TerraGold for their wellness journey.</p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="lissom-cta-section">
          <div className="lissom-cta-overlay"></div>
          <div className="container">
            <div className="lissom-cta-content">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                Ready to Experience <br />
                <span className="lissom-text-gradient">Nature's Purest Nutrition?</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Discover our range of premium organic products crafted for better living
              </motion.p>
              <Link to="/demo" style={{ textDecoration: 'none' }}>
              <motion.button
                className="lissom-cta-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore Our Products
                <FaRocket className="cta-icon" />
              </motion.button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Aboutus;